import { BusinessLogo, Button } from '@components'
import { useBusinessContext, useMobileEmbed } from '@context'
import { useTheme } from '@hooks'
import { SlideContent, SlideFooter } from '@pages/BankIntegrationOnboardingPage/slides/slide.styled'
import {
  EnableBankingAuthorizationFailureVariant,
  EnableBankingPSUType,
  fetchInstitutions,
  fetchRequisition
} from '@query'
import { getRequisitionRedirectURL, showFeaturebase } from '@utils'
import React, { useMemo } from 'react'
import toast from 'react-hot-toast'
import { Trans, useTranslation } from 'react-i18next'
import {
  FaArrowLeft,
  FaComments,
  FaExternalLinkAlt,
  FaPhone,
  FaSync,
  FaUnlink
} from 'react-icons/fa'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'

interface Props {
  onBack: () => void
  onPickAnotherBank: () => void
}

const BANK_LOGO_SIZE = 64

const readPsuType = (value: string | null): EnableBankingPSUType =>
  value === EnableBankingPSUType.PERSONAL
    ? EnableBankingPSUType.PERSONAL
    : EnableBankingPSUType.BUSINESS

export const ConnectionFailedSlide: React.FC<Props> = ({ onBack, onPickAnotherBank }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { search } = useLocation()
  const { slug } = useBusinessContext()
  const {
    isEmbedded,
    redirectURL: embeddedRedirectUrl,
    errorRedirectURL: embeddedErrorRedirectUrl
  } = useMobileEmbed()

  const params = useMemo(() => new URLSearchParams(search), [search])
  const aspspName = params.get('aspsp_name') ?? ''
  const aspspCountry = params.get('aspsp_country') ?? ''
  const psuType = readPsuType(params.get('psu_type'))
  const isOp = params.get('failure_variant') === EnableBankingAuthorizationFailureVariant.OP

  const { data: institutions } = useQuery({
    queryKey: [slug, 'institutions', aspspCountry, psuType],
    queryFn: () => fetchInstitutions({ slug, country: aspspCountry, psuType }),
    enabled: !!aspspCountry
  })
  const bankLogo = institutions?.find(({ name }) => name === aspspName)?.logo

  const { redirectUrl, errorRedirectUrl } = getRequisitionRedirectURL(
    isEmbedded,
    slug,
    embeddedRedirectUrl ?? null,
    embeddedErrorRedirectUrl ?? null
  )

  const { mutate: retry, isPending } = useMutation({
    mutationFn: () =>
      fetchRequisition({
        slug,
        aspspCountry,
        aspspName,
        psuType,
        redirectUrl,
        errorRedirectUrl
      }),
    onSuccess: requisition => {
      if (!requisition.url) {
        toast.error(t('general.error'))
        return
      }
      window.location.href = requisition.url
    },
    onError: () => {
      toast.error(t('general.error'))
    }
  })

  const handleTryAgain = () => {
    if (!aspspName || !aspspCountry) {
      onPickAnotherBank()
      return
    }
    retry()
  }

  const phoneNumber = t('bankOnboarding.failed.op.phoneNumber')

  const tryAgainButton = (
    <Button
      type="button"
      size="lg"
      intent={isOp ? 'default' : 'primary'}
      icon={<FaSync />}
      onClick={handleTryAgain}
      showSpinner={isPending}
    >
      {t('bankOnboarding.failed.tryAgain')}
    </Button>
  )

  return (
    <>
      <SlideContent>
        <StyledContent>
          <StyledLogoPair>
            {bankLogo ? (
              <StyledBankLogo src={bankLogo} alt={aspspName} />
            ) : (
              <BusinessLogo size={BANK_LOGO_SIZE} name={aspspName} />
            )}
            <StyledConnector aria-hidden>
              <StyledDash />
              <StyledBrokenLink>
                <FaUnlink />
              </StyledBrokenLink>
              <StyledDash />
            </StyledConnector>
            <StyledNocfoLogo
              src={theme.assets.logoMark ?? theme.assets.logo}
              $mark={!!theme.assets.logoMark}
              alt=""
            />
          </StyledLogoPair>

          <StyledTitle>
            {t(isOp ? 'bankOnboarding.failed.op.title' : 'bankOnboarding.failed.title')}
          </StyledTitle>
          <StyledDescription $isOp={isOp}>
            {t(isOp ? 'bankOnboarding.failed.op.description' : 'bankOnboarding.failed.description')}
          </StyledDescription>

          {isOp && (
            <>
              <StyledPhoneCard href={`tel:${phoneNumber.replace(/\s/g, '')}`}>
                <StyledPhoneIcon>
                  <FaPhone />
                </StyledPhoneIcon>
                <StyledPhoneText>
                  <StyledPhoneLabel>{t('bankOnboarding.failed.op.phoneLabel')}</StyledPhoneLabel>
                  <StyledPhoneNumber>{phoneNumber}</StyledPhoneNumber>
                </StyledPhoneText>
              </StyledPhoneCard>
              <StyledPhoneMessage>{t('general.or')}</StyledPhoneMessage>
              <StyledPhoneMessage>{t('bankOnboarding.failed.op.phoneAlt')}</StyledPhoneMessage>
            </>
          )}

          <StyledActions $isOp={isOp}>
            {isOp ? (
              <>
                <Button
                  type="button"
                  size="lg"
                  intent="primary"
                  iconRight={<FaExternalLinkAlt />}
                  onClick={() =>
                    window.open(t('link.docs.opBankConnection'), '_blank', 'noopener,noreferrer')
                  }
                >
                  {t('bankOnboarding.failed.op.openGuide')}
                </Button>
                {tryAgainButton}
              </>
            ) : (
              <>
                {tryAgainButton}
                <Button
                  type="button"
                  size="lg"
                  icon={<FaComments />}
                  onClick={() => showFeaturebase('messages')}
                >
                  {t('bankOnboarding.failed.askInChat')}
                </Button>
              </>
            )}
          </StyledActions>

          {!isOp && (
            <StyledHelpLine>
              <Trans i18nKey="bankOnboarding.failed.help">
                <a href={t('link.docs.addingBankIntegration')} target="_blank" rel="noreferrer" />
              </Trans>
            </StyledHelpLine>
          )}
        </StyledContent>
      </SlideContent>
      <SlideFooter>
        <Button type="button" isSecondary icon={<FaArrowLeft />} onClick={onBack}>
          {t('general.back')}
        </Button>
      </SlideFooter>
    </>
  )
}

const StyledContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: 0 ${({ theme }) => theme.spacing.xxl}rem;

  @media (max-width: 768px) {
    padding: 0;
  }
`

const StyledLogoPair = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  margin-bottom: ${({ theme }) => theme.spacing.xxl}rem;
`

const StyledConnector = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const StyledDash = styled.span`
  width: 20px;
  border-top: 2px dashed ${({ theme }) => theme.colors.borderStrong};
`

const StyledBrokenLink = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme }) => theme.dashboard.criticalTint};
  color: ${({ theme }) => theme.colors.error};

  svg {
    width: 15px;
    height: 15px;
  }
`

// Enable Banking logos are mostly wide wordmarks, so size by height like the activation slide.
const StyledBankLogo = styled.img`
  max-width: 140px;
  max-height: 44px;
  object-fit: contain;
`

const StyledNocfoLogo = styled.img<{ $mark: boolean }>`
  height: ${({ $mark }) => ($mark ? 44 : 30)}px;
  width: ${({ $mark }) => ($mark ? '44px' : 'auto')};
  object-fit: contain;
`

const StyledTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-size: ${({ theme }) => theme.fontSize.xxl}rem;
  font-weight: 700;
`

const StyledDescription = styled.p<{ $isOp: boolean }>`
  margin: 0;
  max-width: ${({ $isOp }) => ($isOp ? 440 : 420)}px;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  line-height: 1.5;
  text-wrap: pretty;
`

const StyledPhoneCard = styled.a`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  width: 100%;
  max-width: 360px;
  margin-top: ${({ theme }) => theme.spacing.lg}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  text-align: left;
  color: ${({ theme }) => theme.colors.neutralBlack};
  text-decoration: none !important;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
    color: ${({ theme }) => theme.colors.neutralBlack};
  }
`

const StyledPhoneIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.dashboard.infoTint};
  color: ${({ theme }) => theme.colors.primary};
`

const StyledPhoneText = styled.span`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const StyledPhoneLabel = styled.span`
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const StyledPhoneNumber = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 500;
`

const StyledPhoneMessage = styled.p`
  margin: 0;
  max-width: 360px;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  line-height: 1.5;
`

const StyledActions = styled.div<{ $isOp: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  width: 100%;
  max-width: ${({ $isOp }) => ($isOp ? 360 : 320)}px;
  margin-top: ${({ $isOp, theme }) => ($isOp ? theme.spacing.lg : theme.spacing.xl)}rem;

  button {
    width: 100%;
    justify-content: center;
  }

  button.size-lg svg {
    width: 1rem;
    height: 1rem;
  }
`

const StyledHelpLine = styled.p`
  margin: ${({ theme }) => theme.spacing.sm}rem 0 0;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.px14}rem;

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`
